import { IKnmErknmFormInspector } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormInspector';
import { ISignersForm } from 'KnmErknmPackage/MainModule/interfaces/ISignersForm';

import { IDecisionFormRef } from '../interfaces/IDecisionFormRef';
import { IDecisionActForm } from '../interfaces/IDecisionActForm';
import { IDecisionForm } from '../interfaces/IDecisionForm';
import {
  ACT_FIO_AND_POSITION_SIGNER_FIELD,
  ACT_FIO_SIGNER_FIELD,
  ACT_KNO_INSPECTORS_FIELD,
  ACT_SIGNERS_FIELD,
  ACT_TITLE_SIGNER_FIELD,
  buildInspectorFromIdentity,
  buildKnoInspectorOption,
  buildSignerFioOption,
  canMatchOfficial,
  changeFormField,
  getSignerFio,
  IOfficialIdentity,
  isSameOfficial,
  removeMatchingFieldArrayItems,
} from '../../../KnmPackage/SignersModule/common/actOfficialsSyncCommon';

const clearSignerValues = (form: NonNullable<IDecisionFormRef['current']>, index: number): void => {
  changeFormField(form, `${ACT_SIGNERS_FIELD}[${index}].fioSigner`, undefined);
  changeFormField(form, `${ACT_SIGNERS_FIELD}[${index}].titleSigner`, undefined);
};

/**
 * Удаляет совпадающих подписантов. Если после удаления список стал бы пустым —
 * у последнего совпавшего очищает ФИО/должность вместо удаления строки.
 */
const removeOrClearMatchingSigners = (
  form: NonNullable<IDecisionFormRef['current']>,
  signers: ISignersForm[],
  original: IOfficialIdentity,
): void => {
  const matchingIndexes = signers
    .map((signer, index) => ({ signer, index }))
    .filter(({ signer }) => isSameOfficial(getSignerFio(signer.fioSigner), signer.titleSigner, original))
    .map(({ index }) => index);

  if (!matchingIndexes.length) {
    return;
  }

  const remainingCount = signers.length - matchingIndexes.length;

  if (remainingCount === 0) {
    const [indexToKeep, ...indexesToRemove] = matchingIndexes;

    clearSignerValues(form, indexToKeep);

    indexesToRemove
      .sort((left, right) => right - left)
      .forEach(index => {
        form.mutators?.remove?.(ACT_SIGNERS_FIELD, index);
      });

    return;
  }

  matchingIndexes
    .sort((left, right) => right - left)
    .forEach(index => {
      form.mutators?.remove?.(ACT_SIGNERS_FIELD, index);
    });
};

/**
 * Синхронизирует акт решения с изменением/удалением должностного лица паспорта:
 * - organization.act.knoInspectors
 * - organization.act.signers / fioAndPositionSinger
 */
export const syncDecisionActOfficialsFromPassportInspector = ({
  formRef,
  mode,
  original,
  updated,
}: {
  formRef: IDecisionFormRef | undefined;
  mode: 'update' | 'remove';
  original: IOfficialIdentity;
  updated?: IOfficialIdentity;
}): void => {
  const form = formRef?.current;

  if (!form || !canMatchOfficial(original)) {
    return;
  }

  const act = (form.getState().values as IDecisionForm | undefined)?.organization?.act as IDecisionActForm | undefined;

  if (!act) {
    return;
  }

  const { knoInspectors } = act;

  if (Array.isArray(knoInspectors) && knoInspectors.length > 0) {
    if (mode === 'remove') {
      removeMatchingFieldArrayItems(form, ACT_KNO_INSPECTORS_FIELD, item =>
        isSameOfficial(
          (item as IKnmErknmFormInspector).knoInspector?.value?.fullName,
          (item as IKnmErknmFormInspector).knoInspector?.value?.position,
          original,
        ),
      );
    } else if (updated) {
      knoInspectors.forEach((item, index) => {
        if (!isSameOfficial(item.knoInspector?.value?.fullName, item.knoInspector?.value?.position, original)) {
          return;
        }

        const nextInspectorValue = buildInspectorFromIdentity(updated, undefined, item.knoInspector?.value);

        changeFormField(
          form,
          `${ACT_KNO_INSPECTORS_FIELD}[${index}].knoInspector`,
          buildKnoInspectorOption(nextInspectorValue, item.knoInspector),
        );
      });
    }
  }

  const { signers } = act;

  if (Array.isArray(signers) && signers.length > 0) {
    if (mode === 'remove') {
      removeOrClearMatchingSigners(form, signers, original);
    } else if (updated) {
      signers.forEach((signer, index) => {
        if (!isSameOfficial(getSignerFio(signer.fioSigner), signer.titleSigner, original)) {
          return;
        }

        changeFormField(
          form,
          `${ACT_SIGNERS_FIELD}[${index}].fioSigner`,
          buildSignerFioOption(updated.fullName, signer.fioSigner),
        );
        changeFormField(form, `${ACT_SIGNERS_FIELD}[${index}].titleSigner`, updated.position);
      });
    }
  }

  const { fioAndPositionSinger } = act;

  if (
    fioAndPositionSinger &&
    isSameOfficial(fioAndPositionSinger.value?.fullName, fioAndPositionSinger.value?.position, original)
  ) {
    if (mode === 'remove') {
      changeFormField(form, ACT_FIO_AND_POSITION_SIGNER_FIELD, undefined);
      changeFormField(form, ACT_FIO_SIGNER_FIELD, undefined);
      changeFormField(form, ACT_TITLE_SIGNER_FIELD, undefined);
    } else if (updated) {
      const nextValue = buildInspectorFromIdentity(updated, undefined, fioAndPositionSinger.value);

      changeFormField(
        form,
        ACT_FIO_AND_POSITION_SIGNER_FIELD,
        buildKnoInspectorOption(nextValue, fioAndPositionSinger),
      );
      changeFormField(form, ACT_FIO_SIGNER_FIELD, updated.fullName);
      changeFormField(form, ACT_TITLE_SIGNER_FIELD, updated.position);
    }
  }
};
